import { AssignmentInd } from '@mui/icons-material';
import { Avatar, Box, Divider, Drawer, Theme, Typography, useMediaQuery } from '@mui/material';
import type { FC } from 'react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { Link as RouterLink } from 'react-router';
import { getAvatarName } from '../../helpers/helpers';
import { useAuth } from '../../hooks/useAuth';
import NavSection, { INavSectionItem } from '../navsection/NavSection';
import Scrollbar from '../scrollbar/Scrollbar';

interface DashboardSidebarProps {
    onMobileClose: () => void;
    openMobile: boolean;
}

interface IMenuSection {
    title: string;
    items: INavSectionItem[];
}

const DashboardSidebar: FC<DashboardSidebarProps> = (props) => {
    const { t } = useTranslation();
    const { userName } = useAuth();
    const { onMobileClose, openMobile } = props;
    const location = useLocation();
    const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'));

    const sections: IMenuSection[] = [
        {
            title: `${ t('links_title') }`,
            items: [
                {
                    title: `${ t('links_processes') }`,
                    path: '/processes',
                    icon: <AssignmentInd fontSize="small" />,
                },
            ],
        },
    ];

    useEffect(() => {
        if (openMobile && onMobileClose) {
            onMobileClose();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ location.pathname ]);

    const content = (
        <Box sx={ { display: 'flex', flexDirection: 'column', height: '100%' } }>
            <Scrollbar options={ { suppressScrollX: true } }>
                <Box sx={ { display: { lg: 'none', xs: 'flex' }, justifyContent: 'center', p: 2 } }>
                    <RouterLink to="/"></RouterLink>
                </Box>
                <Box sx={ { p: 2 } }>
                    <Box sx={ { 
                        alignItems: 'center', 
                        background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)',
                        borderRadius: 2, 
                        display: 'flex', 
                        overflow: 'hidden', 
                        p: 2,
                        border: '1px solid rgba(0, 0, 0, 0.06)',
                        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                    } }>
                        { userName && (
                            <Avatar sx={ { 
                                cursor: 'pointer', 
                                height: 48, 
                                width: 48,
                                background: 'linear-gradient(135deg, #2b1f86 0%, #4531e1 100%)',
                                fontWeight: 600,
                                boxShadow: '0 2px 8px rgba(43, 31, 134, 0.2)',
                            } }>
                                { getAvatarName(userName) }
                            </Avatar>
                        ) }
                        <Box sx={ { ml: 2 } }>
                            <Typography color="textPrimary" variant="subtitle2" sx={{ fontWeight: 600 }}>
                                { userName }
                            </Typography>
                        </Box>
                    </Box>
                </Box>
                <Divider />
                <Box sx={ { p: 2 } }>
                    { sections.map((section: IMenuSection) => (
                        <NavSection key={ section.title } pathname={ location.pathname } { ...section } sx={ { '& + &': { mt: 3 } } } />
                    )) }
                </Box>
            </Scrollbar>
        </Box>
    );

    if (lgUp) {
        return (
            <Drawer
                anchor="left"
                open
                PaperProps={ {
                    sx: {
                        backgroundColor: '#ffffff',
                        height: 'calc(100% - 69px) !important',
                        top: '69px !important',
                        width: 280,
                        boxShadow: '4px 0 20px rgba(0, 0, 0, 0.08)',
                        borderRight: '1px solid rgba(0, 0, 0, 0.06)',
                    },
                } }
                variant="permanent">
                { content }
            </Drawer>
        );
    }

    return (
        <Drawer
            anchor="left"
            onClose={ onMobileClose }
            open={ openMobile }
            PaperProps={ {
                sx: {
                    backgroundColor: 'background.paper',
                    width: 280,
                },
            } }
            variant="temporary">
            { content }
        </Drawer>
    );
};

export default DashboardSidebar;
